import {
  parsePreviewAnnotationTarget,
  parsePreviewSnapshot,
  previewAnnotationTargetsEqual,
  type PreviewAnnotationTarget,
  type PreviewSnapshot,
} from './annotation';

export const PREVIEW_CAPTURE_TOOL_NAME = 'preview.capture';
export const PREVIEW_CAPTURE_TIMEOUT_MS = 30_000;
// Voice may wait for a preceding page action before starting the browser capture.
export const VOICE_PREVIEW_CAPTURE_TIMEOUT_MS = 60_000;
export const PREVIEW_CAPTURE_EVENT = 'attune:preview-capture';

export interface PreviewCaptureInput {
  target: PreviewAnnotationTarget;
  pageIndex?: number;
}

export interface PreviewCaptureRequest extends PreviewCaptureInput {
  requestId: string;
  sessionId: string;
  turnId: string;
  projectId: string;
  expiresAt: string;
}

export interface PreviewCapturePage {
  index: number;
  count: number;
  width: number;
  height: number;
}

export type PreviewCaptureResult =
  | {
      status: 'captured';
      target: PreviewAnnotationTarget;
      snapshot: PreviewSnapshot;
      page?: PreviewCapturePage;
    }
  | {
      status: 'unavailable' | 'pending' | 'stale' | 'timeout';
      error: string;
    };

export function parsePreviewCaptureInput(value: unknown): PreviewCaptureInput {
  if (!value || typeof value !== 'object') throw new Error('A preview capture target is required.');
  const input = value as Record<string, unknown>;
  const target = parsePreviewAnnotationTarget(input.target);
  const targetPageIndex = (input.target as Record<string, unknown>).pageIndex;
  // The model selects a page inside its Canvas target; existing browser requests use the root.
  const legacyPageIndex = input.pageIndex;
  if (target.kind === 'website') {
    if (targetPageIndex != null || legacyPageIndex != null) {
      throw new Error(
        'Website captures use target.path. Omit pageIndex and target.pageIndex; page selection is only for Canvas.'
      );
    }
    return { target };
  }
  for (const pageIndex of [targetPageIndex, legacyPageIndex]) {
    if (
      pageIndex != null &&
      (!Number.isSafeInteger(pageIndex) || Number(pageIndex) < 0 || Number(pageIndex) > 199)
    )
      throw new Error('Canvas target.pageIndex must be an integer from 0 to 199, or null.');
  }
  if (targetPageIndex != null && legacyPageIndex != null && targetPageIndex !== legacyPageIndex) {
    throw new Error('Conflicting pageIndex values. Use only Canvas target.pageIndex.');
  }
  const pageIndex = targetPageIndex ?? legacyPageIndex;
  return {
    target,
    ...(pageIndex == null ? {} : { pageIndex: Number(pageIndex) }),
  };
}

export function parsePreviewCaptureResult(
  value: unknown,
  request: PreviewCaptureInput
): PreviewCaptureResult {
  if (!value || typeof value !== 'object') throw new Error('Invalid preview capture result.');
  const result = value as Record<string, unknown>;
  if (result.status !== 'captured') {
    if (
      !['unavailable', 'pending', 'stale', 'timeout'].includes(String(result.status)) ||
      typeof result.error !== 'string' ||
      !result.error.trim()
    ) {
      throw new Error('Invalid preview capture status.');
    }
    return {
      status: result.status as 'unavailable' | 'pending' | 'stale' | 'timeout',
      error: result.error.slice(0, 500),
    };
  }
  const target = parsePreviewAnnotationTarget(result.target);
  if (!previewAnnotationTargetsEqual(target, request.target))
    throw new Error('Preview capture target changed.');
  const snapshot = parsePreviewSnapshot(result.snapshot);
  let page: PreviewCapturePage | undefined;
  if (result.page !== undefined) {
    const input = result.page as PreviewCapturePage;
    if (
      !input ||
      typeof input !== 'object' ||
      !Number.isSafeInteger(input.index) ||
      !Number.isSafeInteger(input.count) ||
      input.index < 0 ||
      input.count < 1 ||
      input.index >= input.count ||
      !Number.isSafeInteger(input.width) ||
      !Number.isSafeInteger(input.height) ||
      input.width < 1 ||
      input.height < 1 ||
      input.width > 16384 ||
      input.height > 16384
    ) {
      throw new Error('Invalid preview capture page.');
    }
    page = { index: input.index, count: input.count, width: input.width, height: input.height };
  }
  if (request.pageIndex !== undefined && page?.index !== request.pageIndex) {
    throw new Error('The requested Canvas page was not captured.');
  }
  return { status: 'captured', target, snapshot, ...(page ? { page } : {}) };
}
